import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface DailyStat {
  date: string;
  requests: number;
  cost: number;
  tokens: number;
}

interface UsageChartProps {
  data: DailyStat[];
  loading?: boolean;
}

export function UsageChart({ data, loading }: UsageChartProps) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Card className="lg:col-span-4">
        <CardHeader>
          <CardTitle>{t('ADMIN_ANALYTICS_USAGETRENDS_TITLE')}</CardTitle>
        </CardHeader>
        <CardContent className="pl-2">
          <div className="h-64 md:h-87.5 w-full bg-muted/20 animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="lg:col-span-4">
      <CardHeader>
        <CardTitle>{t('ADMIN_ANALYTICS_USAGETRENDS_TITLE')}</CardTitle>
      </CardHeader>
      <CardContent className="pl-2">
        <div className="h-64 md:h-87.5 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="date"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />
              <YAxis
                yAxisId="left"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${value}`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  borderColor: 'hsl(var(--border))',
                  borderRadius: 'var(--radius)',
                }}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
              />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="cost"
                name={t('ADMIN_ANALYTICS_USAGETRENDS_COST')}
                fill="hsl(var(--primary))"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                yAxisId="right"
                dataKey="requests"
                name={t('ADMIN_ANALYTICS_USAGETRENDS_REQUESTS')}
                fill="hsl(var(--muted-foreground))"
                radius={[4, 4, 0, 0]}
                opacity={0.3}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
